import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Download } from "lucide-react";
import api from "../lib/api";

/**
 * ScorecardMethodologyPage — renders the versioned KPI methodology
 * doc (`/app/memory/SCORECARD_KPI_METHODOLOGY.md`) so every collaborator
 * can consult the current formula + source for each KPI without leaving
 * the platform. The HTML is server-side rendered by
 * ``routes/scorecard_methodology.py`` so it stays in sync with the code.
 *
 * Extras:
 *   • "Descargar Word" pill → static /Scorecard_KPIs_Cuadro_Consolidado.docx
 *   • "← Volver al Scorecard" link
 */
export default function ScorecardMethodologyPage() {
  const [state, setState] = useState({ loading: true, html: null, error: null });

  useEffect(() => {
    let mounted = true;
    api.get("/scorecard/methodology")
      .then((r) => { if (mounted) setState({ loading: false, html: r.data.html, error: null }); })
      .catch((e) => {
        if (mounted) setState({
          loading: false, html: null,
          error: e?.response?.data?.detail || e.message,
        });
      });
    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#f8fafc" }}>
      <div className="max-w-5xl mx-auto px-6 py-6" data-testid="methodology-page">
        {/* Header bar */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/scorecard"
            data-testid="methodology-back"
            className="inline-flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft size={16}/> Volver al Scorecard
          </Link>
          <a href="/Scorecard_KPIs_Cuadro_Consolidado.docx"
            data-testid="methodology-download-docx"
            download
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border transition-all hover:shadow-sm"
            style={{ borderColor: "#f4c40055", background: "#f4c40014", color: "#a16207" }}>
            <Download size={13}/> Descargar Word
          </a>
        </div>

        {/* Title card */}
        <div className="surface p-6 mb-5" style={{ borderLeft: "4px solid #f4c400" }}>
          <div className="flex items-center gap-3 mb-1">
            <div className="kpi-icon-clean" style={{
              width: 40, height: 40, color: "#f4c400",
              boxShadow: "inset 0 0 0 1px #f4c40033",
            }}>
              <BookOpen size={20}/>
            </div>
            <div>
              <h1 className="font-display font-extrabold text-2xl md:text-3xl"
                style={{ letterSpacing: "-0.02em", color: "#1f2937" }}>
                Metodología KPI Scorecard
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                Fórmula, fuente de datos y estado de automatización por cada KPI del scorecard ejecutivo.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="surface p-6" data-testid="methodology-content">
          {state.loading && (
            <div className="animate-pulse space-y-3">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="h-4 bg-gray-100 rounded"
                  style={{ width: `${60 + (i * 13) % 40}%` }}/>
              ))}
            </div>
          )}
          {state.error && (
            <div className="text-sm text-red-600 font-medium">
              No fue posible cargar la metodología: {state.error}
            </div>
          )}
          {state.html && (
            <div className="methodology-body"
              dangerouslySetInnerHTML={{ __html: state.html }}/>
          )}
        </div>
      </div>

      {/* Prose styles — kept local to avoid touching global CSS. */}
      <style>{`
        .methodology-body { font-family: Manrope, sans-serif; color: #374151; line-height: 1.65; font-size: 14px; }
        .methodology-body h1 { font-size: 22px; font-weight: 800; margin: 24px 0 12px; color: #1f2937; letter-spacing: -0.02em; }
        .methodology-body h2 { font-size: 18px; font-weight: 800; margin: 32px 0 10px; padding-bottom: 6px; border-bottom: 2px solid #f4c400; color: #1f2937; }
        .methodology-body h3 { font-size: 15px; font-weight: 800; margin: 20px 0 8px; color: #374151; }
        .methodology-body h4 { font-size: 13px; font-weight: 800; margin: 14px 0 6px; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; }
        .methodology-body p  { margin: 8px 0; }
        .methodology-body ul, .methodology-body ol { margin: 8px 0 8px 20px; }
        .methodology-body li { margin: 3px 0; }
        .methodology-body strong { color: #1f2937; font-weight: 700; }
        .methodology-body code { background: #f3f4f6; padding: 2px 5px; border-radius: 4px; font-size: 12px; color: #dc2626; font-family: "JetBrains Mono", monospace; }
        .methodology-body pre { background: #1f2937; color: #f9fafb; padding: 12px 14px; border-radius: 8px; overflow-x: auto; font-size: 12px; margin: 12px 0; }
        .methodology-body pre code { background: none; color: inherit; padding: 0; }
        .methodology-body hr { border: none; border-top: 1px solid #e5e7eb; margin: 28px 0; }
        .methodology-body table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 12px; }
        .methodology-body th { background: #1f2937; color: #fff; text-align: left; padding: 8px 10px; font-weight: 700; text-transform: uppercase; font-size: 10px; letter-spacing: 0.04em; }
        .methodology-body td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
        .methodology-body tr:nth-child(even) td { background: #f8fafc; }
        .methodology-body blockquote { border-left: 3px solid #f4c400; padding: 6px 12px; background: #fefce8; color: #713f12; margin: 12px 0; border-radius: 0 6px 6px 0; }
      `}</style>
    </div>
  );
}
